// PerformanceCharts.js
// Three-panel performance visualisation:
//   1. Status Mix donut (Active vs Closed)
//   2. ROI per Trade horizontal bars (closed trades only)
//   3. Closed Trade Outcomes (winner/loser split + avg win/loss)
import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

const DONUT_OPTIONS = {
    cutout: '68%',
    plugins: {
        legend: { display: false },
        tooltip: {
            callbacks: {
                label: (ctx) => ` ${ctx.label}: ${ctx.parsed} trade${ctx.parsed !== 1 ? 's' : ''}`,
            },
        },
    },
};

const PerformanceCharts = ({ trades, selected, stats }) => {
    // ── Donut data ────────────────────────────────────────────────────────────
    const donutData = useMemo(() => ({
        labels: ['Active', 'Closed'],
        datasets: [{
            data: [stats.active || 0, stats.closed || 0],
            backgroundColor: ['#3871E0', '#16a34a'],
            borderColor:     ['#fff',     '#fff'],
            borderWidth: 3,
            hoverOffset: 4,
        }],
    }), [stats.active, stats.closed]);

    // ── Closed trade ROI bars ─────────────────────────────────────────────────
    const closedSelected = trades.filter(
        (t) => selected.includes(t.id) && t.status === 'closed'
    );
    const maxAbs = Math.max(...closedSelected.map((t) => Math.abs(t.pct)), 1);

    // ── Outcome rows ──────────────────────────────────────────────────────────
    const maxOutcome = Math.max(
        Math.abs(stats.avgRoiWin),
        Math.abs(stats.avgLoss),
        stats.maxWin,
        1
    );
    const outcomeRows = [
        { label: 'Avg Win',  val: stats.avgRoiWin, isPos: true  },
        { label: 'Best',     val: stats.maxWin,    isPos: true  },
        { label: 'Avg Loss', val: stats.avgLoss,   isPos: false },
    ];

    const pctLabel = (total, part) =>
        total ? `${Math.round((part / total) * 100)}%` : '0%';

    return (
        <div className="perf-row">
            {/* ── Panel 1: Status Mix donut ──────────────────────────────── */}
            <div className="perf-card">
                <p className="port-section-label">Status</p>
                <div className="donut-wrap">
                    <div style={{ width: 96, height: 96, flexShrink: 0 }}>
                        {stats.total > 0 ? (
                            <Doughnut data={donutData} options={DONUT_OPTIONS} />
                        ) : (
                            /* Empty ring when nothing selected */
                            <svg width="96" height="96" viewBox="0 0 96 96">
                                <circle
                                    cx="48" cy="48" r="32"
                                    fill="none" stroke="#e5e7eb" strokeWidth="18"
                                />
                            </svg>
                        )}
                    </div>
                    <div>
                        <div className="donut-legend-item">
                            <span className="donut-legend-dot" style={{ background: '#3871E0' }} />
                            Active {stats.active} ({pctLabel(stats.total, stats.active)})
                        </div>
                        <div className="donut-legend-item">
                            <span className="donut-legend-dot" style={{ background: '#16a34a' }} />
                            Closed {stats.closed} ({pctLabel(stats.total, stats.closed)})
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Panel 2: ROI per Trade ─────────────────────────────────── */}
            <div className="perf-card">
                <p className="port-section-label">Returns</p>
                {closedSelected.length === 0 ? (
                    <p style={{ fontSize: '0.82rem', color: '#9ca3af', padding: '0.5rem 0' }}>
                        No closed trades selected
                    </p>
                ) : (
                    closedSelected.map((t) => (
                        <div className="roi-bar-row" key={t.id}>
                            <div className="roi-bar-label" title={t.stock}>{t.stock}</div>
                            <div className="roi-bar-track">
                                <div
                                    className={`roi-bar-fill ${t.pct >= 0 ? 'pos' : 'neg'}`}
                                    style={{ width: `${(Math.abs(t.pct) / maxAbs) * 100}%` }}
                                >
                                    {t.pct >= 0 ? '+' : ''}{t.pct.toFixed(1)}%
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* ── Panel 3: Closed Trade Outcomes ────────────────────────── */}
            <div className="perf-card">
                <p className="port-section-label">Closed Outcomes</p>
                {stats.closed > 0 ? (
                    <>
                        {/* Winner / Loser proportional bar */}
                        <div className="winner-loser-bar">
                            <div
                                className="winner-segment"
                                style={{ flex: stats.winners || 0.3 }}
                            >
                                {stats.winners} Positive
                            </div>
                            <div
                                className="loser-segment"
                                style={{ flex: stats.losers || 0.3 }}
                            >
                                {stats.losers} Negative
                            </div>
                        </div>

                        {/* Avg / Max metric bars */}
                        {outcomeRows.map((row) => (
                            <div className="outcome-bar-row" key={row.label}>
                                <div className="outcome-bar-label">{row.label}</div>
                                <div className="outcome-bar-track">
                                    <div
                                        className="outcome-bar-fill"
                                        style={{
                                            width: `${(Math.abs(row.val) / maxOutcome) * 100}%`,
                                            background: row.isPos ? '#dcfce7' : '#fee2e2',
                                            color:      row.isPos ? '#15803d' : '#b91c1c',
                                        }}
                                    >
                                        {row.val > 0 ? '+' : ''}{row.val.toFixed(1)}%
                                    </div>
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    <p style={{ fontSize: '0.82rem', color: '#9ca3af', padding: '0.5rem 0' }}>
                        No closed trades selected
                    </p>
                )}
            </div>
        </div>
    );
};

export default PerformanceCharts;
